import { createClient, type SupabaseClient } from "@supabase/supabase-js";

/**
 * The one place browser code creates a Supabase client. Only ever reads
 * `VITE_`-prefixed env vars - Vite statically inlines exactly those at
 * build time, so the unprefixed server-only secret (read only inside a
 * Netlify Function - see `netlify/functions/_lib/env.ts`) can never end
 * up in this file or the client bundle it produces.
 *
 * Lazily created and cached: importing this module never talks to the
 * network by itself. Returns `null` when the env vars aren't configured
 * (e.g. local dev without them set) so every caller can fail soft into
 * offline mode instead of crashing.
 */
let client: SupabaseClient | null | undefined;

export function getSupabaseClient(): SupabaseClient | null {
  if (client !== undefined) return client;

  const url = import.meta.env.VITE_SUPABASE_URL;
  const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  if (!url || !publishableKey) {
    client = null;
    return client;
  }

  client = createClient(url, publishableKey, {
    auth: { persistSession: true, autoRefreshToken: true },
  });
  return client;
}

export function isSupabaseConfigured(): boolean {
  return getSupabaseClient() !== null;
}

/** Test-only: resets the cached client so each test starts fresh. */
export function __resetSupabaseClientForTests(): void {
  client = undefined;
}
